import { useEffect, useState } from "react";
import { ClipboardList, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export function ReorderDialog({ open, onOpenChange, preset, products, onDone }) {
  const [items, setItems] = useState([{ name: "", qty: 1 }]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setItems([{ name: preset?.name || "", qty: 1 }]); }, [open, preset]);
  const set = (i, patch) => setItems(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/asset-requests", { items: items.map((i) => ({ name: i.name, qty: Number(i.qty) || 1 })), note });
      toast.success("Permintaan barang terkirim ke admin"); setNote(""); onOpenChange(false); onDone?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialog-reorder">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><RotateCcw className="w-5 h-5 text-sky-400" /> Pesan Barang / Alat</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <datalist id="asset-product-names">{(products || []).map((p) => <option key={p.id} value={p.name} />)}</datalist>
          <div className="space-y-2">
            <Label>Barang yang dibutuhkan</Label>
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_72px_32px] gap-1.5" data-testid={`reorder-item-${i}`}>
                <Input required list="asset-product-names" value={it.name} onChange={(e) => set(i, { name: e.target.value })} placeholder="Nama barang" className="h-9 text-sm" data-testid={`reorder-item-name-${i}`} />
                <Input type="number" min="1" required value={it.qty} onChange={(e) => set(i, { qty: e.target.value })} className="h-9 text-sm" data-testid={`reorder-item-qty-${i}`} />
                <Button type="button" size="icon" variant="ghost" className="h-9 w-8 text-rose-400" disabled={items.length === 1} onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`reorder-item-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { name: "", qty: 1 }])} data-testid="reorder-add-item"><Plus className="w-4 h-4 mr-1" /> Tambah</Button>
          </div>
          <div className="space-y-1.5"><Label>Catatan</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="opsional (untuk pemasangan di mana, urgensi)" data-testid="reorder-note" /></div>
          <Button type="submit" disabled={busy} className="w-full bg-sky-600 hover:bg-sky-700 text-white" data-testid="reorder-submit">{busy ? "Mengirim..." : "Kirim Permintaan"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const ST = { diajukan: "bg-amber-500/15 text-amber-400 border-amber-500/30", diproses: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", ditolak: "bg-rose-500/15 text-rose-400 border-rose-500/30" };

export function AssetRequestList({ refreshKey, onFulfil }) {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const [rows, setRows] = useState(null);
  const load = () => api.get("/asset-requests").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line
  const tolak = async (r) => {
    const note = window.prompt(`Tolak permintaan ${r.kt_name}? Tulis alasan (opsional):`);
    if (note === null) return;
    try { await api.post(`/asset-requests/${r.id}/tolak`, { note }); toast.success("Permintaan ditolak"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows || !rows.length) return null;
  return (
    <Card className="bg-card border-border" data-testid="asset-request-list">
      <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center gap-2"><ClipboardList className="w-4 h-4 text-amber-400" /> Permintaan Barang <span className="text-xs font-normal text-muted-foreground">({rows.filter((r) => r.status === "diajukan").length} menunggu)</span></CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="rounded-lg border border-border px-3 py-2 flex items-start justify-between gap-2 text-xs" data-testid={`request-row-${r.id}`}>
            <div className="min-w-0 space-y-0.5">
              <div className="font-semibold text-sm">{r.kt_name}</div>
              <div className="flex flex-wrap gap-1">{r.items.map((i, k) => <span key={k} className="px-2 py-0.5 rounded-full bg-muted border border-border">{i.name} <b className="font-mono text-sky-400">× {i.qty}</b></span>)}</div>
              {r.note && <div className="text-muted-foreground italic">{r.note}</div>}
              <div className="text-[10px] text-muted-foreground">{fmtDateTime(r.created_at)}{r.shipment_code && ` · kiriman ${r.shipment_code}`}{r.reject_note && ` · alasan: ${r.reject_note}`}</div>
            </div>
            <div className="text-right shrink-0 space-y-1">
              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${ST[r.status]}`} data-testid={`request-status-${r.id}`}>{r.status}</span>
              {isAdmin && r.status === "diajukan" && (
                <div className="flex gap-1 justify-end">
                  <Button size="sm" className="h-6 text-[10px] bg-sky-600 hover:bg-sky-700 text-white" onClick={() => onFulfil(r)} data-testid={`request-fulfil-${r.id}`}>Buat Kiriman</Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] text-rose-400" onClick={() => tolak(r)} data-testid={`request-reject-${r.id}`}>Tolak</Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
